/**
 * Cart Drawer — the primary cart experience. Two things happen here:
 *
 * 1. Open/close/backdrop/focus-return on the native <dialog>, same
 *    pattern as snippets/mobile-menu-drawer.liquid + assets/header.js
 *    (backdrop click via event.target === dialog, close event restores
 *    focus, Escape/focus-trap come free from the browser). The slide-in
 *    uses a JS class-toggle (not @starting-style) for the same reason
 *    the collection filter sheet does — see assets/collection-filters.js.
 *
 * 2. Quantity/remove, straight from assets/cart.js: /cart/change.js
 *    keyed by line `key`, then a Section Rendering refresh of this
 *    section's own body so subtotal/shipping/empty-state stay correct
 *    without recomputing any of that here. Kept as a parallel
 *    implementation rather than a shared module — this codebase has no
 *    module system (plain scripts), and the two elements (<cart-items>
 *    on /cart, <cart-drawer> here) are independent DOM trees that can
 *    both be present on the same page (e.g. a user on /cart also has the
 *    drawer in the header) — see the delivery report for the tradeoff.
 *
 * Opens automatically on the `pdp:added-to-cart` event, dispatched by
 * assets/main-product.js (PDP) and assets/product-stories.js (Quick
 * Shop) after a successful /cart/add.js call.
 */
if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.dialog = this.querySelector('dialog');
        if (!this.dialog) return;

        this.body = this.querySelector('[data-cart-drawer-body]');
        this.status = this.querySelector('[data-cart-drawer-status]');
        this.errorBanner = this.querySelector('[data-cart-drawer-error]');
        this.pending = false;
        this.lastTrigger = null;

        this.querySelector('[data-cart-drawer-close]')?.addEventListener('click', () => this.close());

        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });

        this.dialog.addEventListener('close', () => {
          this.lastTrigger?.focus();
        });

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));

        document.addEventListener('pdp:added-to-cart', () => this.openAndRefresh());
      }

      open(trigger) {
        if (trigger) this.lastTrigger = trigger;
        if (this.dialog.open) return;
        this.dialog.showModal();
        this.dialog.classList.remove('is-open');
        // eslint-disable-next-line no-unused-expressions
        this.dialog.offsetHeight;
        setTimeout(() => this.dialog.classList.add('is-open'), 0);
        this.refreshCrossSell();
      }

      close() {
        this.dialog.close();
      }

      async openAndRefresh() {
        this.open();
        await this.refresh();
      }

      onClick(event) {
        if (this.pending) return;

        const decrease = event.target.closest('[data-quantity-decrease]');
        if (decrease) {
          const item = decrease.closest('[data-cart-item]');
          const input = item.querySelector('[data-quantity-input]');
          const previous = Number(input.value) || 1;
          const next = Math.max(1, previous - 1);
          if (next === previous) return;
          input.value = next;
          this.changeLine(item.dataset.lineKey, next, item, false, input, previous);
          return;
        }

        const increase = event.target.closest('[data-quantity-increase]');
        if (increase) {
          const item = increase.closest('[data-cart-item]');
          const input = item.querySelector('[data-quantity-input]');
          const previous = Number(input.value) || 1;
          const next = previous + 1;
          input.value = next;
          this.changeLine(item.dataset.lineKey, next, item, false, input, previous);
          return;
        }

        const remove = event.target.closest('[data-remove-item]');
        if (remove) {
          const item = remove.closest('[data-cart-item]');
          this.changeLine(item.dataset.lineKey, 0, item, true);
          return;
        }

        const crossSellAdd = event.target.closest('[data-cross-sell-add]');
        if (crossSellAdd) {
          this.addFromCrossSell(crossSellAdd);
          return;
        }

        const crossSellToggle = event.target.closest('[data-cross-sell-toggle]');
        if (crossSellToggle) {
          this.toggleCrossSellOptions(crossSellToggle);
          return;
        }

        const crossSellCancel = event.target.closest('[data-cross-sell-cancel]');
        if (crossSellCancel) {
          this.closeAllCrossSellPanels();
          return;
        }

        const crossSellOptionValue = event.target.closest('[data-cross-sell-option-value]');
        if (crossSellOptionValue) {
          this.selectCrossSellOption(crossSellOptionValue);
          return;
        }

        const crossSellConfirm = event.target.closest('[data-cross-sell-confirm]');
        if (crossSellConfirm && !crossSellConfirm.disabled) {
          this.addFromCrossSell(crossSellConfirm);
        }
      }

      onChange(event) {
        if (this.pending) return;
        const input = event.target.closest('[data-quantity-input]');
        if (!input) return;
        const item = input.closest('[data-cart-item]');
        const previous = item.dataset.quantity ? Number(item.dataset.quantity) : Number(input.value) || 1;
        const next = Math.max(1, Number(input.value) || 1);
        input.value = next;
        this.changeLine(item.dataset.lineKey, next, item, false, input, previous);
      }

      async changeLine(key, quantity, itemEl, isRemove, input, previousValue) {
        this.pending = true;
        this.toggleAttribute('aria-busy', true);
        itemEl?.toggleAttribute('aria-busy', true);
        this.hideError();

        try {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity }),
          });

          const cart = await response.json();

          if (!response.ok) {
            if (input && previousValue !== undefined) input.value = previousValue;
            this.showError(cart.description || cart.message || this.dataset.errorAnnouncement);
            return;
          }

          this.updateHeaderCount(cart.item_count);
          await this.refresh();

          if (this.status) {
            this.status.textContent = isRemove ? this.dataset.removedAnnouncement : this.dataset.updatedAnnouncement;
          }
        } catch (error) {
          if (input && previousValue !== undefined) input.value = previousValue;
          this.showError(this.dataset.errorAnnouncement);
        } finally {
          itemEl?.removeAttribute('aria-busy');
          this.pending = false;
          this.toggleAttribute('aria-busy', false);
        }
      }

      /**
       * Shared by both cross-sell paths: the direct "+ Agregar" button on
       * a single-variant product (data-cross-sell-add, variant id baked
       * in server-side) and the inline picker's "Agregar al carrito"
       * button (data-cross-sell-confirm, variant id set by
       * updateCrossSellConfirmState() once a full, available combination
       * is selected — see resolveCrossSellVariant()). Either way, no
       * variant is ever guessed: a missing data-variant-id means the
       * button is disabled and this never fires.
       */
      async addFromCrossSell(button) {
        if (this.pending) return;
        const itemEl = button.closest('[data-cross-sell-item]');
        const variantId = button.dataset.variantId;
        if (!variantId) return;

        const panel = itemEl?.querySelector('[data-cross-sell-options]');
        const isInline = !!(panel && !panel.hidden);

        this.pending = true;
        this.toggleAttribute('aria-busy', true);
        itemEl?.toggleAttribute('aria-busy', true);
        button.disabled = true;
        this.hideError();
        if (isInline) this.hideCrossSellItemError(itemEl);

        const label = button.querySelector('[data-cross-sell-add-label]');

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 }),
          });

          const data = await response.json();

          if (!response.ok) {
            const message = data.description || data.message || this.dataset.errorAnnouncement;
            if (isInline) {
              this.showCrossSellItemError(itemEl, message);
            } else {
              this.showError(message);
            }
            return;
          }

          // /cart/add.js returns the added line, not the full cart — fetch
          // /cart.js for an accurate item_count, same as assets/main-product.js.
          const cart = await (await fetch('/cart.js')).json();
          this.updateHeaderCount(cart.item_count);

          if (label) label.textContent = this.dataset.addedLabel;
          else button.textContent = this.dataset.addedLabel;
          if (this.status) this.status.textContent = this.dataset.updatedAnnouncement;

          // Update subtotal/shipping/items immediately ("sentirse
          // inmediato" — see the delivery report). refreshBody() also
          // resets the cross-sell rail to its empty shell (a plain
          // section_id fetch doesn't populate `recommendations`), so the
          // just-added item's "Agregado ✓" state is re-injected right
          // after, held on screen for a beat, then replaced by the real
          // next set of recommendations.
          const confirmedItemHtml = itemEl.outerHTML;
          await this.refreshBody();
          const freshContainer = this.querySelector('[data-cross-sell]');
          if (freshContainer) freshContainer.innerHTML = confirmedItemHtml;

          await new Promise((resolve) => {
            setTimeout(resolve, 1200);
          });
          await this.refreshCrossSell();
        } catch (error) {
          const message = this.dataset.errorAnnouncement;
          if (isInline) {
            this.showCrossSellItemError(itemEl, message);
          } else {
            this.showError(message);
          }
        } finally {
          button.disabled = false;
          this.pending = false;
          this.toggleAttribute('aria-busy', false);
        }
      }

      toggleCrossSellOptions(toggleButton) {
        const itemEl = toggleButton.closest('[data-cross-sell-item]');
        const panel = itemEl?.querySelector('[data-cross-sell-options]');
        if (!panel) return;

        const wasOpen = !panel.hidden;
        this.closeAllCrossSellPanels();

        if (!wasOpen) {
          panel.hidden = false;
          toggleButton.setAttribute('aria-expanded', 'true');
          this.updateCrossSellConfirmState(itemEl);
        }
      }

      closeAllCrossSellPanels() {
        this.querySelectorAll('[data-cross-sell-options]').forEach((panel) => {
          panel.hidden = true;
          const item = panel.closest('[data-cross-sell-item]');
          item?.querySelector('[data-cross-sell-toggle]')?.setAttribute('aria-expanded', 'false');
        });
      }

      selectCrossSellOption(button) {
        const group = button.closest('[data-cross-sell-option-group]');
        const itemEl = button.closest('[data-cross-sell-item]');
        if (!group || !itemEl) return;

        group.querySelectorAll('[data-cross-sell-option-value]').forEach((candidate) => {
          const active = candidate === button;
          candidate.classList.toggle('cart-drawer-cross-sell-item__option--active', active && candidate.classList.contains('cart-drawer-cross-sell-item__option'));
          candidate.classList.toggle('cart-drawer-cross-sell-item__swatch--active', active && candidate.classList.contains('cart-drawer-cross-sell-item__swatch'));
          candidate.setAttribute('aria-pressed', String(active));
        });

        this.updateCrossSellConfirmState(itemEl);
      }

      /**
       * Compact, client-side only — no combo-availability-per-button
       * refinement like sections/product-variant-picker.liquid has for
       * the full PDP (deliberately: this is a compact cross-sell surface,
       * not a second PDP). It only ever enables "Agregar al carrito" once
       * every option group has a selection AND that exact combination
       * resolves to a real, available variant — never a guess, never
       * first_available_variant.
       */
      // Reads the option buttons currently marked aria-pressed="true" per
      // group. Sparse on purpose — a group with nothing selected yet is
      // simply absent from `selections`, which is what lets
      // getCrossSellVariantsMatching() below treat it as a wildcard.
      getCrossSellSelections(itemEl) {
        const groups = Array.from(itemEl.querySelectorAll('[data-cross-sell-option-group]'));
        const selections = {};
        let complete = true;

        groups.forEach((group) => {
          const index = Number(group.dataset.optionIndex);
          const active = group.querySelector('[data-cross-sell-option-value][aria-pressed="true"]');
          if (active) {
            selections[index] = active.dataset.value;
          } else {
            complete = false;
          }
        });

        return { groups, selections, complete };
      }

      resolveCrossSellVariant(itemEl) {
        const script = itemEl.querySelector('[data-cross-sell-variants]');
        if (!script) return { complete: false, variant: null };

        let variants;
        try {
          variants = JSON.parse(script.textContent);
        } catch (error) {
          return { complete: false, variant: null };
        }

        const { selections, complete } = this.getCrossSellSelections(itemEl);
        if (!complete) return { complete: false, variant: null };

        const indexes = Object.keys(selections).map(Number);
        const variant = variants.find((v) => indexes.every((i) => v.options[i] === selections[i])) || null;
        return { complete: true, variant };
      }

      updateCrossSellConfirmState(itemEl) {
        const confirmBtn = itemEl.querySelector('[data-cross-sell-confirm]');
        if (!confirmBtn) return;

        this.hideCrossSellItemError(itemEl);
        this.syncCrossSellVariantDisplay(itemEl);

        const { complete, variant } = this.resolveCrossSellVariant(itemEl);

        if (!complete || !variant || !variant.available) {
          confirmBtn.disabled = true;
          confirmBtn.textContent = !complete ? this.dataset.addToCartLabel : this.dataset.soldOutLabel;
          delete confirmBtn.dataset.variantId;
          return;
        }

        confirmBtn.disabled = false;
        confirmBtn.textContent = this.dataset.addToCartLabel;
        confirmBtn.dataset.variantId = variant.id;
      }

      /**
       * Live visual sync for the Inline Quick Add — runs on every option
       * click, not just once a full combination resolves. Looks at every
       * variant still compatible with the CURRENT (possibly partial)
       * selection:
       *   - image updates the instant that whole compatible set shares one
       *     media (e.g. every "Negro" variant uses the same photo regardless
       *     of Talla) — never guesses when the set still spans >1 image.
       *   - price/compare-at only update once the selection narrows to
       *     exactly one variant, same as before.
       * Never blanks the image — if nothing is unambiguous yet, the current
       * (fallback/original) image simply stays put.
       */
      syncCrossSellVariantDisplay(itemEl) {
        const script = itemEl.querySelector('[data-cross-sell-variants]');
        if (!script) return;

        let variants;
        try {
          variants = JSON.parse(script.textContent);
        } catch (error) {
          return;
        }

        const { selections } = this.getCrossSellSelections(itemEl);
        const indexes = Object.keys(selections).map(Number);
        const matching = variants.filter((v) => indexes.every((i) => v.options[i] === selections[i]));
        if (matching.length === 0) return;

        const uniqueImages = [...new Set(matching.map((v) => v.image || null))];
        if (uniqueImages.length === 1 && uniqueImages[0]) {
          this.setCrossSellImage(itemEl, uniqueImages[0]);
        }

        if (matching.length === 1) {
          this.setCrossSellPrice(itemEl, matching[0]);
        }
      }

      setCrossSellImage(itemEl, imageUrl) {
        const image = itemEl.querySelector('.cart-drawer-cross-sell-item__image');
        if (!image || image.src === imageUrl) return;
        image.classList.add('cart-drawer-cross-sell-item__image--swapping');
        image.src = imageUrl;
        image.removeAttribute('srcset');
        requestAnimationFrame(() => image.classList.remove('cart-drawer-cross-sell-item__image--swapping'));
      }

      setCrossSellPrice(itemEl, variant) {
        const priceEl = itemEl.querySelector('[data-cross-sell-price]');
        if (priceEl && typeof variant.price === 'number') {
          priceEl.textContent = this.formatMoney(variant.price);
        }
      }

      // Every price observed in this catalog is whole pesos (no cents) —
      // a plain thousands-separated peso format matches
      // money_without_trailing_zeros closely enough for this small,
      // client-side-only display update.
      formatMoney(cents) {
        const amount = Math.round(cents / 100).toLocaleString('es-MX');
        return `$ ${amount}`;
      }

      showCrossSellItemError(itemEl, message) {
        const errorEl = itemEl?.querySelector('[data-cross-sell-item-error]');
        if (!errorEl) return;
        errorEl.textContent = message;
        errorEl.hidden = false;
      }

      hideCrossSellItemError(itemEl) {
        const errorEl = itemEl?.querySelector('[data-cross-sell-item-error]');
        if (!errorEl) return;
        errorEl.hidden = true;
      }

      /**
       * Cross-sell is fetched separately from the main body refresh
       * because its content only exists when Shopify's recommendations
       * endpoint actually processes the request (routes.product_recommendations_url
       * with a product_id) — a plain Section Rendering fetch of this
       * section (used by refresh() for subtotal/shipping/items) doesn't
       * populate `recommendations`, so the container would just come back
       * empty every time otherwise. Tries "complementary" first (explicit
       * priority), falls back to "related" only if that comes back empty —
       * see the delivery report for why complementary alone isn't reliable
       * yet on this store.
       */
      async refreshCrossSell() {
        const container = this.querySelector('[data-cross-sell]');
        if (!container) return;

        const { recommendationsUrl, sectionIdParam, anchorProductId } = container.dataset;
        if (!recommendationsUrl || !anchorProductId) return;

        for (const intent of ['complementary', 'related']) {
          try {
            // limit=10: a bigger candidate pool for the Liquid-side
            // diversity picker (sections/cart-drawer.liquid) to choose a
            // complementary pair from — still only ever renders 2.
            const url = `${recommendationsUrl}?section_id=${sectionIdParam}&product_id=${anchorProductId}&limit=10&intent=${intent}`;
            // eslint-disable-next-line no-await-in-loop
            const response = await fetch(url);
            // eslint-disable-next-line no-await-in-loop
            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('[data-cross-sell]');

            if (fresh && fresh.innerHTML.trim() !== '') {
              // Only innerHTML is replaced — the container keeps its own
              // data-* attributes (recommendationsUrl/sectionIdParam/
              // anchorProductId) for the next call.
              container.innerHTML = fresh.innerHTML;
              return;
            }
          } catch (error) {
            /* try the next intent, or leave the container empty */
          }
        }

        container.innerHTML = '';
      }

      showError(message) {
        if (!this.errorBanner) return;
        this.errorBanner.textContent = message;
        this.errorBanner.hidden = false;
      }

      hideError() {
        if (!this.errorBanner) return;
        this.errorBanner.hidden = true;
      }

      async refresh() {
        await this.refreshBody();
        await this.refreshCrossSell();
      }

      // Just the main section render (header count/subtotal/shipping/
      // items/empty-state) — split out from refresh() so
      // addFromCrossSell() can update those immediately while holding the
      // cross-sell rail's own "Agregado ✓" confirmation on screen a beat
      // longer (see that method for why).
      async refreshBody() {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', this.sectionId);

        const response = await fetch(url.toString());
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newBody = doc.querySelector('[data-cart-drawer-body]');

        if (newBody && this.body) {
          const previousScroll = this.body.querySelector('.cart-drawer__scroll')?.scrollTop || 0;
          const oldShipping = this.body.querySelector('[data-cart-drawer-shipping]');
          const oldWidth = oldShipping?.querySelector('.cart-drawer-shipping__fill')?.style.width;
          const oldReached = oldShipping?.classList.contains('cart-drawer-shipping--reached');
          this.body.innerHTML = newBody.innerHTML;
          const scroller = this.body.querySelector('.cart-drawer__scroll');
          if (scroller && previousScroll) scroller.scrollTop = previousScroll;

          // The section swap recreates the bar, so replay old -> new to keep the width/color transition.
          const shipping = this.body.querySelector('[data-cart-drawer-shipping]');
          const fill = shipping?.querySelector('.cart-drawer-shipping__fill');
          if (fill && oldWidth) {
            const targetWidth = fill.style.width;
            const targetReached = shipping.classList.contains('cart-drawer-shipping--reached');
            fill.style.width = oldWidth;
            shipping.classList.toggle('cart-drawer-shipping--reached', Boolean(oldReached));
            fill.getBoundingClientRect();
            fill.style.width = targetWidth;
            shipping.classList.toggle('cart-drawer-shipping--reached', targetReached);
          }
        }
      }

      updateHeaderCount(count) {
        const link = document.querySelector('.site-header__cart-link');
        if (!link) return;
        let badge = link.querySelector('.site-header__cart-count');

        if (count > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'site-header__cart-count';
            badge.setAttribute('aria-hidden', 'true');
            link.appendChild(badge);
          }
          badge.textContent = count;
        } else if (badge) {
          badge.remove();
        }
      }
    }
  );
}

/**
 * Progressive enhancement: the header cart link keeps its real
 * href="/cart" for no-JS / failed-script visitors. When this script
 * runs, it takes over the click and opens the drawer instead.
 */
(function initCartDrawerTrigger() {
  const trigger = document.querySelector('[data-cart-drawer-trigger]');
  const drawer = document.querySelector('cart-drawer');
  if (!trigger || !drawer) return;

  trigger.addEventListener('click', (event) => {
    event.preventDefault();
    drawer.open(trigger);
  });
})();
